<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { createEventDispatcher } from 'svelte';
  import AgeSelector from '$lib/components/AgeSelector.svelte';
  
  const dispatch = createEventDispatcher();
  
  function handlePlay(event: CustomEvent<{ year: number; month: number }>) {
    const { year, month } = event.detail;
    navigate(`/${year}/${month}`);
    dispatch('navigate');
  }
</script>

<svelte:head>
  <title>outlive.it - An idle game that takes 104 years to complete</title>
</svelte:head>

<!-- Banner -->
<section id="intro" class="main">
  <AgeSelector on:play={handlePlay} />
</section>

<!-- Items -->
<section class="main items">
  <article class="item">
    <header>
      <a
        href="/"
        on:click|preventDefault
        style:cursor="initial"
        style:background-image="url('/assets/images/book.webp')"
        aria-hidden="true"
        tabindex="-1"
      ></a>
      <h3>How does this work?</h3>
    </header>
    <p>
      We meticulously scour through our Ecclesiastical Database and compare the Papal Records
      to your age. You will see your ranking and progress.
    </p>
  </article>
  
  <article class="item">
    <header>
      <a
        href="/"
        on:click|preventDefault
        style:cursor="initial"
        style:background-image="url('/assets/images/mailbox.webp')"
        aria-hidden="true"
        tabindex="-1"
      ></a>
      <h3>Track your progress</h3>
    </header>
    <p>
      You can subscribe to email notifications. They are sent whenever you outlive a pope or an antipope.
      You will get at most 302 emails over 104 years, so on average one every four months.
      Pope John XI died the youngest at the age of 25, so consider your first 25 years as tutorial to the game.
    </p>
  </article>
  
  <article class="item">
    <header>
      <a
        href="/about"
        style:background-image="url('/assets/images/books.webp')"
        aria-label="About this game"
        on:click|preventDefault={() => navigate('/about')}
      ></a>
      <h3>About</h3>
    </header>
    <ul class="actions">
      <li><a href="/about" class="button" on:click|preventDefault={() => navigate('/about')}>About this game</a></li>
    </ul>
  </article>

  <article class="item">
    <header>
      <a
        href="/privacy"
        style:background-image="url('/assets/images/privacy.webp')"
        aria-label="Privacy statement"
        on:click|preventDefault={() => navigate('/privacy')}
      ></a>
      <h3>Privacy statement</h3>
    </header>
    <ul class="actions">
      <li><a href="/privacy" class="button" on:click|preventDefault={() => navigate('/privacy')}>About privacy</a></li>
    </ul>
  </article>
</section>

<style>
  .items {
    display: flex;
    flex-wrap: wrap;
    width: calc(100% + 2em);
    margin: -2em 0 0 -2em;
  }

  .item {
    padding: 3em 3em 1em 3em;
    background-color: #ffffff;
    border-radius: 4px;
    text-align: center;
    margin: 2em 0 0 2em;
    width: calc(50% - 2em);
  }

  .item :global(header) {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    margin: -3em 0 3em -3em;
    width: calc(100% + 6em);
    height: 15em;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #444;
    color: rgba(255, 255, 255, 0.75);
    position: relative;
    overflow: hidden;
  }

  .item :global(header a) {
    transition: transform 0.2s ease;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: block;
  }

  .item :global(header a::before) {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.25);
  }

  .item :global(header:hover a) {
    transform: scale(1.1);
  }

  .item :global(header h3) {
    margin: 0;
    pointer-events: none;
    position: relative;
    z-index: 1;
    color: #ffffff;
  }

  #intro {
    padding: 4em;
    background-color: #ffffff;
    border-radius: 4px;
    text-align: center;
  }

  .actions {
    list-style: none;
    padding: 0;
  }

  @media screen and (max-width: 980px) {
    .items {
      flex-direction: column;
      width: 100%;
      margin: 0;
    }

    .item {
      width: 100%;
      margin: 0 0 2em 0;
    }

    .item :global(header) {
      height: 25em;
    }
  }

  @media screen and (max-width: 736px) {
    #intro {
      padding: 2em;
    }

    .item :global(header) {
      height: 15em;
    }
  }

  @media screen and (max-width: 480px) {
    #intro {
      padding: 2em 1em;
    }

    .item {
      padding: 2em 1em 0.1em 1em;
    }

    .item :global(header) {
      height: 12em;
      margin: -2em 0 2em -1em;
      width: calc(100% + 2em);
    }
  }
</style>
